import React, { useEffect, useState, forwardRef } from "react";
import { icons } from "oolib";
import styled from "styled-components";
import { Dropdown, Input, InputGroup, Button } from "@/components/UI/CompactControls";
import {
  MODE_OPTIONS,
  clampToBounds as clamp,
  getNumberRangeMode,
  convertNumberRangeValue,
} from "./numberRangeMode";

const { ChartBarHorizontal } = icons;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
`;

const StyledField = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;

  & > * {
    flex: 1;
    min-width: 0;
  }
`;

// Histogram (distribution) button — generic Button + a ChartBarHorizontal
// rotated -90deg (→ vertical bars). forwardRef so it can be a
// <LightboxTrigger asChild> target.
export const HistogramButton = forwardRef(({ style, ...props }, ref) => (
  <Button
    ref={ref}
    aria-label="Show distribution"
    style={{ width: 24, padding: 0, flexShrink: 0, ...style }}
    {...props}
  >
    <div style={{ display: "flex", transform: "rotate(-90deg)" }}>
      <ChartBarHorizontal size={16} />
    </div>
  </Button>
));

/**
 * NumberRangeCompactInput — compact Value/Range filter editor, composed from
 * the generic CompactControls atoms. Mode is inferred from the value shape:
 * { exact } => "value", { min, max } => "range".
 *
 * @param {{ min?: number, max?: number, exact?: number }} value
 * @param {Function} onChange - (newValue) => void
 * @param {number} min - lower bound (clamping)
 * @param {number} max - upper bound (clamping)
 * @param {React.ReactNode} [histogramButton] - replaces the default histogram
 *   button (e.g. a <LightboxTrigger>-wrapped <HistogramButton/>); Range mode only
 * @param {Function} [onHistogramClick] - onClick for the default histogram button
 */
export const NumberRangeCompactInput = ({
  value = {},
  onChange,
  min = 0,
  max = 100,
  histogramButton,
  onHistogramClick,
  // While bounds are still loading (or the bounds fetch failed), min/max are the
  // 0–100 placeholder and clamping typed values against them silently truncates
  // real values. Disable editing until real bounds arrive.
  disabled = false,
}) => {
  const mode = getNumberRangeMode(value);

  // Local display state so the user can type freely; parent gets clamped values.
  const [minInput, setMinInput] = useState(value?.min ?? min);
  const [maxInput, setMaxInput] = useState(value?.max ?? max);
  const [valueInput, setValueInput] = useState(value?.exact ?? min);

  useEffect(() => {
    setMinInput(value?.min ?? min);
    setMaxInput(value?.max ?? max);
    setValueInput(value?.exact ?? min);
  }, [value?.min, value?.max, value?.exact, min, max]);

  const handleModeChange = (newMode) => {
    if (disabled) return;
    if (newMode === mode) return;
    onChange(convertNumberRangeValue(value, newMode, min, max));
  };

  const handleMinChange = (e) => {
    if (disabled) return;
    const val = e.target.value;
    setMinInput(val);
    const num = parseFloat(val);
    if (!isNaN(num)) onChange({ min: clamp(num, min, max), max: value?.max ?? max });
  };

  const handleMaxChange = (e) => {
    if (disabled) return;
    const val = e.target.value;
    setMaxInput(val);
    const num = parseFloat(val);
    if (!isNaN(num)) onChange({ min: value?.min ?? min, max: clamp(num, min, max) });
  };

  const handleValueChange = (e) => {
    if (disabled) return;
    const val = e.target.value;
    setValueInput(val);
    const num = parseFloat(val);
    if (!isNaN(num)) onChange({ exact: clamp(num, min, max) });
  };

  return (
    // stop clicks from bubbling to a parent that toggles a lightbox
    <StyledRow onClick={(e) => e.stopPropagation()}>
      <Dropdown options={MODE_OPTIONS} value={mode} onChange={handleModeChange} disabled={disabled} />

      {mode === "value" ? (
        <StyledField>
          <InputGroup>
            <Input type="number" placeholder="Value" value={valueInput} onChange={handleValueChange} disabled={disabled} />
          </InputGroup>
        </StyledField>
      ) : (
        <>
          <StyledField>
            <InputGroup>
              <Input type="number" placeholder="From" value={minInput} onChange={handleMinChange} disabled={disabled} />
              <Input type="number" placeholder="To" value={maxInput} onChange={handleMaxChange} disabled={disabled} />
            </InputGroup>
          </StyledField>
          {histogramButton || <HistogramButton onClick={onHistogramClick} />}
        </>
      )}
    </StyledRow>
  );
};
